import { csrfToken } from "../utils/csrf_token"
import { showToast } from "../utils/show_toast"
import { Controller } from "@hotwired/stimulus"
import { getMarkRange } from "@tiptap/core"
import { createTipTapEditor, linkPanelHighlightDecorationKey } from "../tiptap/editor"

export default class extends Controller {
  static targets = ["source", "mount", "form", "linkBubble", "linkUrl", "linkRemove", "toolbarLink", "imageInput"]

  static values = {
    proseClass: { type: String, default: "tiptap-prose" },
    imageUploadUrl: { type: String, default: "" },
  }

  connect() {
    this.linkPanelOpen = false
    this.linkPanelAnchorFrom = null
    this.linkPanelAnchorTo = null
    this.linkBubbleRepositionRaf = null
    this.linkBubbleOpenRaf = null
    this.linkBubbleLayoutResizeObserver = null
    this.boundCloseLinkPanelOnOutsidePointerDown = this.closeLinkPanelOnOutsidePointerDown.bind(this)
    this.boundScheduleLinkBubbleReposition = this.scheduleLinkBubbleReposition.bind(this)

    this.editor = createTipTapEditor({
      element: this.mountTarget,
      content: this.sourceTarget.value || "",
      editorProps: {
        attributes: { class: this.proseClassValue },
      },
      imageResize: Boolean(this.imageUploadUrlValue),
    })
    this.knownImageSrcs = this.imageSrcs()
    this.editor.on("update", () => this.syncRemovedImages())
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.boundCloseLinkPanelOnOutsidePointerDown, true)
    if (this.linkBubbleOpenRaf != null) {
      cancelAnimationFrame(this.linkBubbleOpenRaf)
      this.linkBubbleOpenRaf = null
    }
    this.stopLinkBubbleLayoutTracking()
    this.editor?.destroy()
    this.editor = null
    this.linkPanelOpen = false
    this.linkPanelAnchorFrom = null
    this.linkPanelAnchorTo = null
    if (this.hasLinkBubbleTarget) {
      this.clearLinkBubblePosition()
      this.linkBubbleTarget.classList.remove("tiptap-editor-link-bubble--measuring")
      this.linkBubbleTarget.classList.add("tiptap-editor-link-bubble--hidden")
    }
  }

  preserveToolbarSelection(event) {
    if (event.button !== 0) return
    event.preventDefault()
  }

  prepareSubmit() {
    if (this.linkPanelOpen) this.closeLinkPanel(true)
    this.sourceTarget.value = this.editor.getHTML()
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run()
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run()
  }

  toggleStrike() {
    this.editor.chain().focus().toggleStrike().run()
  }

  toggleUnderline() {
    this.editor.chain().focus().toggleUnderline().run()
  }

  toggleHeading(event) {
    const level = parseInt(event.params.level, 10)
    this.editor.chain().focus().toggleHeading({ level }).run()
  }

  toggleBulletList() {
    this.editor.chain().focus().toggleBulletList().run()
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run()
  }

  toggleBlockquote() {
    this.editor.chain().focus().toggleBlockquote().run()
  }

  toggleCodeBlock() {
    this.editor.chain().focus().toggleCodeBlock().run()
  }

  toggleCode() {
    this.editor.chain().focus().toggleCode().run()
  }

  insertHorizontalRule() {
    this.editor.chain().focus().setHorizontalRule().run()
  }

  setImageAlt(signedId, alt) {
    this.updateImageAttrs(signedId, { alt })
  }

  setImageCaption(signedId, caption) {
    this.updateImageAttrs(signedId, { caption })
  }

  removeImage(signedId) {
    this.suppressImageDelete = true
    this.imagePositions(signedId).forEach((pos) => {
      const node = this.editor.state.doc.nodeAt(pos)
      if (!node) return
      this.editor.view.dispatch(this.editor.state.tr.delete(pos, pos + node.nodeSize))
    })
    this.suppressImageDelete = false
    this.knownImageSrcs = this.imageSrcs()
  }

  syncRemovedImages() {
    if (this.suppressImageDelete || !this.imageUploadUrlValue) return

    const current = this.imageSrcs()
    const removed = [...this.knownImageSrcs].filter((src) => !current.has(src))
    this.knownImageSrcs = current
    removed.forEach((src) => this.deleteInlineImage(src))
  }

  imageSrcs() {
    const srcs = new Set()
    if (!this.editor) return srcs

    this.editor.state.doc.descendants((node) => {
      if (node.type.name !== "image" || !node.attrs.src) return
      srcs.add(node.attrs.src)
    })
    return srcs
  }

  deleteInlineImage(src) {
    const signedId = this.signedIdFromImageSrc(src)
    if (!signedId) return

    fetch(`${this.imageUploadUrlValue}/signed?signed_id=${encodeURIComponent(signedId)}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken(),
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json",
      },
    }).then((response) => {
      if (!response.ok) {
        showToast("Failed to delete image", "error")
        return
      }
      this.element.dispatchEvent(new CustomEvent("article-inline-image:server-deleted", {
        bubbles: true,
        detail: { signedId }
      }))
      showToast("Image deleted")
    }).catch(() => {
      showToast("Failed to delete image", "error")
    })
  }

  signedIdFromImageSrc(src) {
    let path = String(src || "")
    try {
      path = new URL(path, window.location.origin).pathname
    } catch {
      return null
    }
    const match = path.match(/\/rails\/active_storage\/(?:blobs|representations)\/(?:redirect|proxy)\/([^/]+)/)
    return match ? decodeURIComponent(match[1]) : null
  }

  imagePositions(signedId) {
    if (!this.editor || !signedId) return []

    const positions = []
    this.editor.state.doc.descendants((node, pos) => {
      if (node.type.name !== "image") return
      if (!String(node.attrs.src || "").includes(signedId)) return
      positions.push(pos)
    })
    return positions.sort((a, b) => b - a)
  }

  updateImageAttrs(signedId, attrs) {
    this.imagePositions(signedId).forEach((pos) => {
      const node = this.editor.state.doc.nodeAt(pos)
      if (!node) return
      this.editor.view.dispatch(
        this.editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs })
      )
    })
  }

  insertImage() {
    if (!this.hasImageInputTarget) return
    this.imageInputTarget.click()
  }

  imageChosen() {
    const file = this.imageInputTarget.files?.[0]
    this.imageInputTarget.value = ""
    if (!file || !this.imageUploadUrlValue) return

    const body = new FormData()
    body.append("file", file)

    fetch(this.imageUploadUrlValue, {
      method: "POST",
      body,
      headers: {
        "X-CSRF-Token": csrfToken(),
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json",
      },
    }).then(async (response) => {
      if (!response.ok) {
        showToast("Failed to upload image", "error")
        return
      }
      const data = await response.json()
      if (!data.url) {
        showToast("Failed to upload image", "error")
        return
      }
      this.editor.chain().focus().setImage({ src: data.url, alt: data.alt || "", align: "left" }).run()
      if (data.card_html) {
        this.element.dispatchEvent(new CustomEvent("article-inline-image:inserted", {
          bubbles: true,
          detail: { html: data.card_html }
        }))
      }
      this.saveBody()
    }).catch(() => {
      showToast("Failed to upload image", "error")
    })
  }

  saveBody() {
    if (!this.editor || !this.hasFormTarget) return

    const html = this.editor.getHTML()
    this.sourceTarget.value = html
    fetch(this.formTarget.action, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json",
      },
      body: JSON.stringify({ article: { body: html } }),
    }).then((response) => {
      if (!response.ok) {
        showToast("Failed to save the post", "error")
        return
      }
      this.articleEditor()?.rememberSavedBody(html)
    }).catch(() => {
      showToast("Failed to save the post", "error")
    })
  }

  articleEditor() {
    const root = this.element.closest("[data-controller~='article-editor']")
    if (!root) return null
    return this.application.getControllerForElementAndIdentifier(root, "article-editor")
  }

  toggleSuperscript() {
    this.editor.chain().focus().toggleSuperscript().run()
  }

  toggleSubscript() {
    this.editor.chain().focus().toggleSubscript().run()
  }

  toggleHighlightColor(event) {
    const color = event.params.highlightColor
    if (!color) return
    this.editor.chain().focus().toggleHighlight({ color }).run()
  }

  clearHighlight() {
    this.editor.chain().focus().unsetHighlight().run()
  }

  setTextAlign(event) {
    const alignment = event.params.align
    const selected = this.editor.state.selection.node
    if (selected?.type?.name === "image") {
      this.editor.chain().focus().updateAttributes("image", { align: alignment }).run()
      this.saveBody()
      return
    }
    this.editor.chain().focus().setTextAlign(alignment).run()
  }

  setColor(event) {
    const color = event.params.color
    if (color) {
      this.editor.chain().focus().setColor(color).run()
    } else {
      this.editor.chain().focus().unsetColor().run()
    }
  }

  insertTable() {
    this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
  }

  addTableColumnBefore() {
    this.editor.chain().focus().addColumnBefore().run()
  }

  addTableColumnAfter() {
    this.editor.chain().focus().addColumnAfter().run()
  }

  addTableRowBefore() {
    this.editor.chain().focus().addRowBefore().run()
  }

  addTableRowAfter() {
    this.editor.chain().focus().addRowAfter().run()
  }

  deleteTableColumn() {
    this.editor.chain().focus().deleteColumn().run()
  }

  deleteTableRow() {
    this.editor.chain().focus().deleteRow().run()
  }

  deleteTable() {
    this.editor.chain().focus().deleteTable().run()
  }

  toggleLinkPanel() {
    if (this.linkPanelOpen) {
      this.closeLinkPanel()
      return
    }
    const { empty } = this.editor.state.selection
    if (empty && !this.editor.isActive("link")) {
      return
    }
    this.openLinkPanel()
  }

  computeLinkPanelAnchorRange() {
    const { state } = this.editor
    const { from, to } = state.selection
    if (from === to && this.editor.isActive("link")) {
      const linkType = state.schema.marks.link
      const range = getMarkRange(state.doc.resolve(from), linkType)
      if (range) return { anchorFrom: range.from, anchorTo: range.to }
    }
    return { anchorFrom: from, anchorTo: to }
  }

  anchorRangeHasLink(from, to) {
    const linkType = this.editor.state.schema.marks.link
    if (!linkType) return false
    if (to > from) {
      return this.editor.state.doc.rangeHasMark(from, to, linkType)
    }
    const marks = this.editor.state.doc.resolve(from).marks()
    return marks.some((m) => m.type === linkType)
  }

  setLinkBubblePosition(leftPx, topPx) {
    if (!this.hasLinkBubbleTarget) return
    const el = this.linkBubbleTarget
    el.style.setProperty("--tiptap-editor-link-bubble-left", `${leftPx}px`)
    el.style.setProperty("--tiptap-editor-link-bubble-top", `${topPx}px`)
  }

  clearLinkBubblePosition() {
    if (!this.hasLinkBubbleTarget) return
    const el = this.linkBubbleTarget
    el.style.removeProperty("--tiptap-editor-link-bubble-left")
    el.style.removeProperty("--tiptap-editor-link-bubble-top")
  }

  // Single layout pass: never set a throwaway position first (that ran every scroll/resize and flickered).
  placeLinkBubble() {
    if (!this.linkPanelOpen) return
    if (!this.hasToolbarLinkTarget || !this.hasLinkBubbleTarget) return
    const btnRect = this.toolbarLinkTarget.getBoundingClientRect()
    const bubble = this.linkBubbleTarget
    const bubbleRect = bubble.getBoundingClientRect()
    if (bubbleRect.width === 0 || bubbleRect.height === 0) return

    const bodyRect = this.element.getBoundingClientRect()
    let top = btnRect.top - bubbleRect.height - 8
    let left = btnRect.left
    if (top < 4) top = btnRect.bottom + 8

    const margin = 8
    const minLeft = bodyRect.left + margin
    const maxLeft = bodyRect.right - margin - bubbleRect.width
    if (maxLeft >= minLeft) {
      left = Math.min(Math.max(left, minLeft), maxLeft)
    } else {
      left = minLeft
    }
    this.setLinkBubblePosition(left, top)
  }

  scheduleLinkBubbleReposition() {
    if (!this.linkPanelOpen) return
    if (this.linkBubbleRepositionRaf != null) return
    this.linkBubbleRepositionRaf = requestAnimationFrame(() => {
      this.linkBubbleRepositionRaf = null
      this.placeLinkBubble()
    })
  }

  startLinkBubbleLayoutTracking() {
    window.addEventListener("resize", this.boundScheduleLinkBubbleReposition)
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.boundScheduleLinkBubbleReposition)
      window.visualViewport.addEventListener("scroll", this.boundScheduleLinkBubbleReposition)
    }
    document.addEventListener("scroll", this.boundScheduleLinkBubbleReposition, true)
    if (typeof ResizeObserver !== "undefined") {
      this.linkBubbleLayoutResizeObserver = new ResizeObserver(() => this.scheduleLinkBubbleReposition())
      this.linkBubbleLayoutResizeObserver.observe(this.element)
    }
  }

  stopLinkBubbleLayoutTracking() {
    if (this.linkBubbleRepositionRaf != null) {
      cancelAnimationFrame(this.linkBubbleRepositionRaf)
      this.linkBubbleRepositionRaf = null
    }
    window.removeEventListener("resize", this.boundScheduleLinkBubbleReposition)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.boundScheduleLinkBubbleReposition)
      window.visualViewport.removeEventListener("scroll", this.boundScheduleLinkBubbleReposition)
    }
    document.removeEventListener("scroll", this.boundScheduleLinkBubbleReposition, true)
    if (this.linkBubbleLayoutResizeObserver) {
      this.linkBubbleLayoutResizeObserver.disconnect()
      this.linkBubbleLayoutResizeObserver = null
    }
  }

  openLinkPanel() {
    const { anchorFrom, anchorTo } = this.computeLinkPanelAnchorRange()
    this.linkPanelAnchorFrom = anchorFrom
    this.linkPanelAnchorTo = anchorTo

    const href = this.editor.getAttributes("link").href || ""
    this.linkUrlTarget.value = href

    if (anchorTo > anchorFrom) {
      this.editor.view.dispatch(
        this.editor.state.tr.setMeta(linkPanelHighlightDecorationKey, {
          from: anchorFrom,
          to: anchorTo,
        }),
      )
    }

    const hasLink = this.anchorRangeHasLink(anchorFrom, anchorTo)
    this.linkRemoveTarget.classList.toggle("tiptap-editor-link-remove--hidden", !hasLink)

    this.linkBubbleTarget.classList.remove("tiptap-editor-link-bubble--hidden")
    this.linkBubbleTarget.classList.add("tiptap-editor-link-bubble--measuring")
    this.linkPanelOpen = true
    this.linkBubbleTarget.setAttribute("aria-hidden", "false")
    this.startLinkBubbleLayoutTracking()
    this.linkBubbleOpenRaf = requestAnimationFrame(() => {
      this.linkBubbleOpenRaf = null
      this.placeLinkBubble()
      this.linkBubbleTarget.classList.remove("tiptap-editor-link-bubble--measuring")
      this.linkUrlTarget.focus()
      this.linkUrlTarget.select()
    })
    document.addEventListener("pointerdown", this.boundCloseLinkPanelOnOutsidePointerDown, true)
  }

  closeLinkPanelOnOutsidePointerDown(event) {
    if (!this.linkPanelOpen) return
    if (this.hasLinkBubbleTarget && this.linkBubbleTarget.contains(event.target)) return
    if (this.hasToolbarLinkTarget && this.toolbarLinkTarget.contains(event.target)) return
    this.closeLinkPanel(false, { focusEditor: false })
  }

  closeLinkPanel(skipRestore = false, options = {}) {
    if (!this.linkPanelOpen) return
    const { focusEditor = true } = options
    if (this.linkBubbleOpenRaf != null) {
      cancelAnimationFrame(this.linkBubbleOpenRaf)
      this.linkBubbleOpenRaf = null
    }
    this.stopLinkBubbleLayoutTracking()
    document.removeEventListener("pointerdown", this.boundCloseLinkPanelOnOutsidePointerDown, true)
    const skipRestoreSelection = skipRestore === true
    if (!skipRestoreSelection) {
      this.restoreLinkPanelAnchorSelection()
    }
    this.linkPanelOpen = false
    this.linkBubbleTarget.setAttribute("aria-hidden", "true")
    this.clearLinkBubblePosition()
    this.linkBubbleTarget.classList.remove("tiptap-editor-link-bubble--measuring")
    this.linkBubbleTarget.classList.add("tiptap-editor-link-bubble--hidden")
    if (this.editor?.view) {
      this.editor.view.dispatch(this.editor.state.tr.setMeta(linkPanelHighlightDecorationKey, false))
    }
    this.linkPanelAnchorFrom = null
    this.linkPanelAnchorTo = null
    if (focusEditor) {
      this.editor?.chain().focus().run()
    }
  }

  restoreLinkPanelAnchorSelection() {
    if (this.linkPanelAnchorFrom == null || this.linkPanelAnchorTo == null) return
    this.editor
      .chain()
      .focus()
      .setTextSelection({ from: this.linkPanelAnchorFrom, to: this.linkPanelAnchorTo })
      .run()
  }

  linkUrlKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.applyLink()
    }
    if (event.key === "Escape") {
      event.preventDefault()
      this.closeLinkPanel()
    }
  }

  applyLink() {
    this.restoreLinkPanelAnchorSelection()
    let url = this.linkUrlTarget.value.trim()

    if (url === "") {
      if (this.editor.isActive("link")) {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
      }
      this.closeLinkPanel(true)
      return
    }

    url = this.normalizeLinkUrl(url)
    const chain = this.editor.chain().focus()
    if (this.editor.isActive("link")) {
      chain.extendMarkRange("link").setLink({ href: url }).run()
    } else {
      chain.setLink({ href: url }).run()
    }
    this.closeLinkPanel(true)
  }

  removeLink() {
    this.restoreLinkPanelAnchorSelection()
    if (this.editor.isActive("link")) {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
    }
    this.closeLinkPanel(true)
  }

  normalizeLinkUrl(url) {
    if (/^mailto:/i.test(url) || /^tel:/i.test(url)) {
      return url
    }
    if (url.startsWith("/") && !url.startsWith("//")) {
      return url
    }
    if (/^https?:\/\//i.test(url)) {
      return url
    }
    return `https://${url}`
  }

  undo() {
    this.editor.chain().focus().undo().run()
  }

  redo() {
    this.editor.chain().focus().redo().run()
  }
}
